import { SearchableCardOption } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";
import { CellData } from "./table";
import { TableCell } from "./tableCell/tableCell";

/**
 * Props for the custom table body renderer.
 *
 * @property computedCellData - Pre-computed cell matrix (rows × columns) from the table shell.
 * @property tableDataToRender - Raw row objects passed through on row-click handlers.
 * @property rowClassName - Static class or per-row function applied to each `<tr>`.
 */
interface TableBodyProps {
  computedCellData: CellData[][];
  tableDataToRender: any[];
  tableWidth: number;
  onRowClick: (tableData: any[], rowIndex: number, className: string) => void;
  onAssignToClick?: (data: any) => void;
  onStatusClick?: (data: any) => void;
  onDeleteIconClick?: (data: any) => void;
  onEditIconClick?: (data: any) => void;
  onIconCellClick?: (data: any) => void;
  onClickableTextWithDisabledStatusClick?: (data: any) => void;
  onComplete?: () => void;
  /** Fired when a CLICKABLE_BADGE_WITH_ON_CLICK cell is clicked; receives the cell `id`. */
  onClickableBadgeClick?: (id: string) => void;
  toggleCheckboxSelection?: (id: string) => void;
  handleDownloadButtonClick?: (id: string) => void;
  onValueChange?: (value: string) => void;
  onTableRowUpdate?: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void;
  startIndex?: number;
  onSideModalClose?: () => void;
  onChangeEmit?: (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => void;
  onPropagateEventOnClickInRpaStatusCell?: (id: string) => void;
  onPropagateEventOnClickInRpaStatusTwoCell?: (id: string) => void;
  onRpaStatusForExternalClick?: (
    modalType: "image" | "doc",
    orderId: string,
  ) => void;
  onValueChangeOfTextField?: (value: string, id: string, type?: string) => void;
  onOverlappingIconClick?: (icon_id: string, order_id: string) => void;
  rowClassName?: string | ((rowData: any, rowIndex: number) => string);
  onUploadClick?: (
    orderId: string,
    jCode: string,
    jCodeId: string,
    authStatus: string,
    authLetterReviewStatus?: string,
  ) => void;
  onSuccessClick?: (
    orderId: string,
    jCode: string,
    jCodeId: string,
    authStatus: string,
    authLetterReviewStatus?: string,
  ) => void;
  handleIconClickOnDocumentScreenshot?: (rowData: any) => void;
  /** Fired when the user types in a SEARCHABLE_CARD cell's search input. `id` is the cell value's id. */
  onSearchChange?: (searchTerm: string, id: string) => void;
  /** Fired when the user creates a new option in a SEARCHABLE_CARD cell. `id` is the cell value's id. */
  onCreateOption?: (value: string, id: string) => void;
  /** Fired when the user clicks an option in a SEARCHABLE_CARD cell. `id` is the cell value's id. */
  onOptionClick?: (option: SearchableCardOption, id: string) => void;
}

/**
 * Renders the `<tbody>` for the custom table, mapping each row of
 * {@link computedCellData} to a flex `<tr>` and delegating cell rendering to
 * {@link TableCell}.
 *
 * Rows use a stable React `key` (`jCodeId`, then `id`, then index fallback)
 * so reordering data (e.g. CPT/J-code rows) does not reuse stale component
 * state from a previous row at the same index.
 *
 * @param props - See {@link TableBodyProps}.
 * @returns The table body element.
 */
const TableBody: React.FC<TableBodyProps> = ({
  computedCellData,
  tableDataToRender,
  tableWidth,
  onRowClick,
  onAssignToClick,
  onStatusClick,
  onDeleteIconClick,
  onEditIconClick,
  onIconCellClick,
  onClickableTextWithDisabledStatusClick,
  onComplete,
  onClickableBadgeClick,
  toggleCheckboxSelection,
  handleDownloadButtonClick,
  onTableRowUpdate,
  onValueChange,
  startIndex,
  onSideModalClose,
  onChangeEmit,
  onPropagateEventOnClickInRpaStatusCell,
  onPropagateEventOnClickInRpaStatusTwoCell,
  onRpaStatusForExternalClick,
  onValueChangeOfTextField,
  onOverlappingIconClick,
  rowClassName,
  onUploadClick,
  onSuccessClick,
  handleIconClickOnDocumentScreenshot,
  onSearchChange,
  onCreateOption,
  onOptionClick,
}) => {
  const handleTableRowUpdate = (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => {
    onTableRowUpdate?.(rowIndex, fieldKey, newValue);
  };

  const handleValueChange = (value: string) => {};

  return (
    <tbody>
      {computedCellData.map((rowData, rowIndex) => {
        const currentRow = rowData[0]?.rowData;
        const rowKey =
          currentRow?.jCodeId ?? currentRow?.id ?? `row-${rowIndex}`;
        const extraRowClass =
          typeof rowClassName === "function"
            ? rowClassName(currentRow, rowIndex)
            : (rowClassName ?? "");
        return (
          <tr
            key={rowKey}
            style={{ display: "flex", width: "100%" }}
            className={extraRowClass}
          >
            {rowData.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={`table-cell ${cell.type}__${cell.header}`}
                style={{
                  width: `${cell?.width}%`,
                }}
                onClick={() =>
                  onRowClick(
                    tableDataToRender,
                    rowIndex,
                    `table-cell ${cell.type}__${cell.header}`,
                  )
                }
              >
                <TableCell
                  type={cell.type}
                  id={cell.rowData?.id}
                  value={cell.value}
                  header={cell.header}
                  rowIndex={rowIndex}
                  globalRowIndex={startIndex ? startIndex + rowIndex : rowIndex}
                  rowData={cell.rowData}
                  handleAssignToClick={onAssignToClick}
                  handleStatusClick={onStatusClick}
                  handleDeleteIconClick={onDeleteIconClick}
                  handleEditIconClick={onEditIconClick}
                  handleIconCellClick={onIconCellClick}
                  handleClickableTextWithDisabledStatusClick={
                    onClickableTextWithDisabledStatusClick
                  }
                  onComplete={onComplete}
                  onClickableBadgeClick={onClickableBadgeClick}
                  toggleCheckboxSelection={toggleCheckboxSelection}
                  handleDownloadButtonClick={handleDownloadButtonClick}
                  onTableRowUpdate={handleTableRowUpdate}
                  onValueChange={handleValueChange}
                  onSideModalClose={onSideModalClose}
                  onChangeEmit={onChangeEmit}
                  onPropagateEventOnClickInRpaStatusCell={
                    onPropagateEventOnClickInRpaStatusCell
                  }
                  onPropagateEventOnClickInRpaStatusTwoCell={
                    onPropagateEventOnClickInRpaStatusTwoCell
                  }
                  onRpaStatusForExternalClick={onRpaStatusForExternalClick}
                  onValueChangeOfTextField={onValueChangeOfTextField}
                  onOverlappingIconClick={onOverlappingIconClick}
                  onUploadClick={onUploadClick}
                  onSuccessClick={onSuccessClick}
                  handleIconClickOnDocumentScreenshot={
                    handleIconClickOnDocumentScreenshot
                  }
                  onSearchChange={onSearchChange}
                  onCreateOption={onCreateOption}
                  onOptionClick={onOptionClick}
                />
              </td>
            ))}
          </tr>
        );
      })}
    </tbody>
  );
};

export default TableBody;
